import React from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { MdOutlineCalendarMonth } from "react-icons/md";
import mapImg from "../../../assets/images/map.png";
import MapsView from "../../NewProjectDetails/components/ProjectOverview/MapsView";

const AVATAR_COLORS = [
  "#4D6BF6",
  "#FA832E",
  "#22C55E",
  "#A855F7",
  "#EC4899",
  "#06B6D4",
  "#F59E0B",
  "#EF4444",
  "#14B8A6",
  "#8B5CF6",
];

const colorFromName = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const Avatar = ({ name }) => {
  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <Box
      sx={{
        width: 35,
        height: 35,
        borderRadius: "50%",
        backgroundColor: colorFromName(name || ""),
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        fontWeight: 600,
        flexShrink: 0,
        boxShadow: "0px 4px 2.67px 0px #00000026",
      }}
    >
      {initial}
    </Box>
  );
};

const PersonRow = ({ name, email }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
    <Avatar name={name} />
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 14,
          color: "#0D0D0E",
          fontWeight: 500,
          fontFamily: "Inter Tight",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {name || "—"}
      </Typography>
      {email ? (
        <Typography
          sx={{
            fontSize: 12,
            color: "#949DAA",
            fontFamily: "Inter Tight",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {email}
        </Typography>
      ) : null}
    </Box>
  </Box>
);

const TIMELINE_PHASES = [
  "Approvals",
  "Demolition & Site Prep",
  "Construction",
  "Finishing & Handover",
];

const TIMELINE_COLORS = {
  Approvals: "#4D6BF6",
  "Demolition & Site Prep": "#FA832E",
  Construction: "#22C55E",
  "Finishing & Handover": "#A855F7",
};

const parseTimeline = (timeline) => {
  if (!timeline) return null;
  if (typeof timeline === "object") return timeline;
  try {
    return JSON.parse(timeline);
  } catch {
    return null;
  }
};

const computeWeekRanges = (timeline) => {
  if (!timeline) return [];
  const ranges = [];
  let cursor = 1;
  TIMELINE_PHASES.forEach((phase) => {
    const weeks = parseInt(timeline?.[phase]) || 0;
    if (weeks > 0) {
      const startWeek = cursor;
      const endWeek = cursor + weeks - 1;
      ranges.push({
        phase,
        startWeek,
        endWeek,
        weeks,
        color: TIMELINE_COLORS[phase],
      });
      cursor = endWeek + 1;
    }
  });
  return ranges;
};

const SectionHeading = ({ children, icon }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      color: "#0D0D0E",
      fontWeight: 600,
      fontSize: 16,
      fontFamily: "Inter Tight",
      mb: 1.5,
    }}
  >
    {icon}
    {children}
  </Box>
);

const TimelineTrack = ({ ranges }) => (
  <Box
    sx={{
      display: "flex",
      backgroundColor: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      overflow: "hidden",
      flexWrap: { xs: "wrap", sm: "nowrap" },
    }}
  >
    {ranges.map((r, i) => (
      <Box
        key={i}
        sx={{
          flex: { xs: `1 1 50%`, sm: r.weeks },
          padding: "14px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          borderRight: { xs: "none", sm: "1px solid #F3F4F6" },
          borderBottom: { xs: "1px solid #F3F4F6", sm: "none" },
          position: "relative",
          "&:last-of-type": {
            borderRight: "none",
            borderBottom: "none",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "3px",
            backgroundColor: r.color,
          },
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: r.color }}>
          Week{" "}
          {r.startWeek === r.endWeek
            ? r.startWeek
            : `${r.startWeek}-${r.endWeek}`}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#0D0D0E", fontWeight: 500 }}>
          {r.phase}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#6B7280" }}>
          {r.weeks} {r.weeks === 1 ? "week" : "weeks"}
        </Typography>
      </Box>
    ))}
  </Box>
);

const sectionLabel = {
  fontSize: 13,
  color: "#949DAA",
  fontWeight: 500,
  fontFamily: "Inter Tight",
  marginBottom: 0,
};
const sectionValue = {
  fontSize: 14,
  color: "#0D0D0E",
  fontFamily: "Inter Tight",
  whiteSpace: "pre-wrap",
  marginTop: 2,
};

export default function Overview({ proposal }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (!proposal) {
    return (
      <Box sx={{ p: 2, color: "#9CA3AF", textAlign: "center" }}>
        No details available
      </Box>
    );
  }

  const timeline = parseTimeline(proposal.timeline);
  const weekRanges = computeWeekRanges(timeline);
  const projectLocation =
    proposal.location_address || proposal.location || "";
  const customerName = proposal.customer_name || "";
  const customerEmail = proposal.customer_email || "";
  const contractorName =
    proposal?.Contractor?.company_name ||
    proposal?.Contractor?.username ||
    proposal?.contractor_name ||
    "";
  const contractorEmail =
    proposal?.Contractor?.email || proposal?.contractor_email || "";
  const proposalDescription =
    proposal.description || proposal.scope_of_work || proposal.scope || "";
  const hasCoords =
    proposal?.lat != null && proposal?.lng != null;
  const smallMapContainerStyle = {
    width: isMobile ? "30vw" : "200px",
    height: isMobile ? "80px" : "120px",
    borderRadius: "12px",
    pointerEvents: "none",
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: "1.5rem", md: "2vw" },
        py: 1,
      }}
    >
      <Box sx={{ flex: 2, display: "flex", flexDirection: "column", gap: 3 }}>
        <Box>
          <SectionHeading
            icon={<MdOutlineCalendarMonth color="#4D6BF6" size={20} />}
          >
            Project Timeline
          </SectionHeading>
          {weekRanges.length > 0 ? (
            <TimelineTrack ranges={weekRanges} />
          ) : (
            <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
              No timeline added.
            </Typography>
          )}
        </Box>

        <Box>
          <Typography sx={{ ...sectionLabel, mb: 0.25 }}>
            Scope of work
          </Typography>
          <Typography sx={{ ...sectionValue, mt: 0 }}>
            {proposalDescription || "—"}
          </Typography>
        </Box>
      </Box>

      {isMobile && (
        <Box sx={{ borderTop: "1px solid #D7D8D9", width: "100%" }} />
      )}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
          }}
        >
          Members
        </Typography>

        <Box>
          <Typography sx={{ ...sectionLabel, mb: 1 }}>Customer</Typography>
          <PersonRow name={customerName} email={customerEmail} />
        </Box>

        {(contractorName || contractorEmail) && (
          <Box>
            <Typography sx={{ ...sectionLabel, mb: 1 }}>Contractor</Typography>
            <PersonRow name={contractorName} email={contractorEmail} />
          </Box>
        )}

        <Box>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#0D0D0E",
              fontFamily: "Inter Tight",
              mb: 1,
            }}
          >
            Location
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <Typography
              sx={{
                fontSize: isMobile ? "13px" : "14px",
                color: "#0D0D0E",
                fontFamily: "Inter Tight",
                flex: 1,
                minWidth: 0,
                wordBreak: "normal",
                overflowWrap: "anywhere",
                whiteSpace: "normal",
              }}
            >
              {projectLocation || "N/A"}
            </Typography>
            {hasCoords ? (
              <Box style={{ pointerEvents: "none" }}>
                <MapsView
                  details={proposal}
                  mapContainerStyle={smallMapContainerStyle}
                  interactive={false}
                />
              </Box>
            ) : (
              <Box
                component="img"
                src={mapImg}
                alt="Map"
                sx={{
                  height: isMobile ? 150 : 75,
                  width: isMobile ? "80vw" : 120,
                  objectFit: "cover",
                  borderRadius: "12px",
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
